import AuthGroup from "../../configs/authgroup";
import Permissions from "../../configs/permission";
import { jwtDecode } from "jwt-decode";

/**
 * หน้าปลายทางเริ่มต้นของ user — ใช้ 2 จุด:
 * 1. หลัง login (แทนที่จะพาไป `/home` เสมอ — user ที่ไม่มี `Dashboard.VIEW` จะเข้าไม่ได้)
 * 2. เมื่อเข้า route ที่ไม่มีสิทธิ์แล้ว route นั้นสั่งให้พากลับหน้าที่เข้าได้ (`deniedGoToLanding`)
 *
 * ⚠️ **ลำดับต้องตรงกับเมนูใน `Components/Layout/Admin.jsx`** และเงื่อนไข bypass ต้องตรงกับ
 * `PrivateRoute` ใน `router.jsx` — เพิ่มเมนูใหม่แล้วลืมเพิ่มที่นี่ = user ที่มีสิทธิ์เฉพาะเมนูนั้น
 * จะตกไปหน้า `/no-access` ทั้งที่กดเมนูเข้าได้
 */
const LANDING_ROUTES = [
  { path: "/home", permissions: [Permissions.Dashboard?.VIEW] },
  // bypass เดียวกับ PrivateRoute: กลุ่ม DestinationWarehouse ใช้โมดูล company ได้เทียบเท่า superadmin
  { path: "/company", permissions: [Permissions.Company?.VIEW], bypassGroups: [AuthGroup.DestinationWarehouse] },
  // bypass เดียวกับ PrivateRoute: กลุ่ม CompanySale เข้าหน้า customer เพื่อค้นหาลูกค้าของ sale ตัวเอง
  { path: "/customer", permissions: [Permissions.Customer?.VIEW], bypassGroups: [AuthGroup.CompanySale] },
  { path: "/sales", permissions: [Permissions.Sale?.VIEW] },
  { path: "/package/originpackage", permissions: [Permissions.OriginPackage?.VIEW] },
  { path: "/package/originpackagetransportation", permissions: [Permissions.OriginPackageTransportation?.VIEW] },
  { path: "/package/destinationpackage", permissions: [Permissions.DestinationPackage?.VIEW] },
  { path: "/package/destinationdelivery", permissions: [Permissions.DestinationDelivery?.VIEW] },
  { path: "/package/destinationpackagedelivery", permissions: [Permissions.DestinationPackageDelivery?.VIEW] },
  { path: "/bill/companybill", permissions: [Permissions.CompanyBill?.VIEW] },
  { path: "/bill/customerbill", permissions: [Permissions.CustomerBill?.VIEW] },
  { path: "/warehouses", permissions: [Permissions.Warehouse?.VIEW] },
  { path: "/usermanagement/user", permissions: [Permissions.User?.VIEW] },
  { path: "/usermanagement/group", permissions: [Permissions.Group?.VIEW] },
];

/** ไม่มีสิทธิ์เข้าหน้าไหนเลย → หน้าแจ้งเตือน (ไม่เตะออกจากระบบ ผู้ใช้จะได้รู้ว่าต้องไปขอสิทธิ์) */
export const NO_ACCESS_PATH = "/no-access";

/** หน้าแรกที่ user เข้าได้ตามลำดับเมนู · superuser = dashboard เสมอ */
export const resolveLandingPath = ({ permissions = [], isSuperuser = false, groups = [] } = {}) => {
  if (isSuperuser) return LANDING_ROUTES[0].path;

  const allowed = LANDING_ROUTES.find(
    (route) =>
      route.permissions.some((perm) => perm != null && permissions.includes(perm)) ||
      (route.bypassGroups ?? []).some((group) => groups.includes(group))
  );
  return allowed?.path ?? NO_ACCESS_PATH;
};

/** อ่านสิทธิ์จาก JWT แล้วหาหน้าแรก — ใช้ตอน login ที่ยังไม่มี state ของสิทธิ์ในมือ */
export const resolveLandingPathFromToken = (token) => {
  if (!token) return NO_ACCESS_PATH;
  try {
    const decoded = jwtDecode(token);
    return resolveLandingPath({
      permissions: Array.isArray(decoded?.permission) ? decoded.permission : [],
      isSuperuser: Boolean(decoded?.is_superuser),
      groups: Array.isArray(decoded?.group_id) ? decoded.group_id : [],
    });
  } catch (err) {
    console.error("[landingPath] resolveLandingPathFromToken: ", err);
    return NO_ACCESS_PATH;
  }
};
